import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    productPriceSavingsMessageStyles,
    ProductPriceSavingsMessageStyles,
} from "@stoneage/Overrides/Components/ProductPriceSavingsMessage";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    isAuthenticated:
        (state.context.session.isAuthenticated || state.context.session.rememberMe) && !state.context.session.isGuest,
    hideProductBundleOptions: (getSettingsCollection(state) as any).customSettings?.["hideProductBundleOptions"],
    sessionCurrency: state.context.session.currency,
});

export interface ProductDetailsPriceStyles {
    productPrice?: ProductPriceStyles;

    wrapper?: InjectableCss;
    availability?: ProductAvailabilityStyles;
    savingPriceStyles?: ProductPriceSavingsMessageStyles;
    variantParentPriceReplacement?: InjectableCss;
    replacementPrice?: TypographyProps;
    replacementInventory?: TypographyProps;
    replacementInventoryIcon?: IconPresentationProps;
}

export const priceStyles: ProductDetailsPriceStyles = {
    productPrice: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                flex-grow: 1;
                line-height: 20px;
                padding-right: 5px;
            `,
        },
        price: {
            priceText: { size: 20, lineHeight: "24px" },
            unitOfMeasureText: { size: 26 },
        },
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            width: 100%;
            margin-bottom: 12px;
        `,
    },
    availability: {
        container: {
            css: css`
                width: auto;
            `,
        },
        errorWrapper: {
            css: css`
                text-align: center;
            `,
        },
        errorText: {
            weight: "bold",
            size: "12px",
            lineHeight: "16px",
            css: css`
                white-space: pre-wrap;
            `,
        },
    },
    savingPriceStyles: {
        ...productPriceSavingsMessageStyles,
        wrapper: {
            css: css`
                display: flex;
                align-items: center;
                gap: 8px;
                white-space: nowrap;
                flex-wrap: wrap;
            `,
        },
        label: {
            size: 12,
            lineHeight: "18px",
            color: "text.main",
            weight: "bold",
            css: css`
                opacity: 0.5;
            `,
        },
        price: {
            size: 15,
            color: "text.main",
            lineHeight: "26px",
            css: css`
                font-weight: 800;
            `,
        },
    },
    variantParentPriceReplacement: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            margin-bottom: 12px;
            line-height: 14px;
            &.not-pdp {
                margin-top: 18px;
                margin-bottom: 0;
                width: 100%;
            }
        `,
    },
    replacementPrice: {
        size: "20px",
        lineHeight: "24px",
        css: css`
            font-weight: 800;
            flex-grow: 1;
            &.not-pdp {
                font-size: 16px;
                line-height: 22px;
                color: ${getColor("common.black")};
            }
        `,
    },
    replacementInventory: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            display: flex;
            align-items: center;
            white-space: nowrap;
            &.not-pdp {
                line-height: 21px;
            }
        `,
    },
    replacementInventoryIcon: {
        css: css`
            margin-right: 5px;
        `,
    },
};

const styles = priceStyles;

const ProductDetailsPrice: React.FC<OwnProps> = ({
    productContext,
    productSettings,
    configurationCompleted,
    isAuthenticated,
    hideProductBundleOptions,
    sessionCurrency,
}) => {
    if (!productContext.product || !isAuthenticated || productContext.product?.cantBuy) {
        return null;
    }

    const { product, productInfo } = productContext;

    const isConfigurableProduct =
        !!product.configurationType && product.configurationType !== "None" && product.configurationType !== "Fixed";

    const shouldShowReplacement =
        (product.isVariantParent || isConfigurableProduct) && isAuthenticated && !configurationCompleted;

    const shouldHideInventoryForBundleProducts =
        hideProductBundleOptions?.toLowerCase() === "true" &&
        product.properties["hideBundleOptions"]?.toLowerCase() === "true" &&
        isConfigurableProduct;

    const pricingRenderValue = (
        <>
            {!productInfo.pricing || productInfo.pricing?.regularPrice === 0 ? (
                <Typography {...styles.replacementPrice} translate="no">
                    {sessionCurrency?.currencySymbol || sessionCurrency?.currencyCode}–.––
                </Typography>
            ) : (
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showUnitOfMeasure={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.productPrice}
                    {...({ priceSavingExtendedStyles: styles.savingPriceStyles } as any)}
                />
            )}
        </>
    );

    if (shouldShowReplacement) {
        return (
            <StyledWrapper {...styles.variantParentPriceReplacement}>
                {pricingRenderValue}
                {!shouldHideInventoryForBundleProducts && (
                    <Typography {...styles.replacementInventory}>
                        <Icon src="IconGear" size={12} {...styles.replacementInventoryIcon} />
                        {siteMessage("Custom_Inventory_ConfigureForAvailability", "Configure for Availability")}
                    </Typography>
                )}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {pricingRenderValue}
            {productContext.product.isVariantParent && (
                <Typography {...styles.replacementInventory}>
                    <Icon src="IconGear" size={12} {...styles.replacementInventoryIcon} />
                    {translate("Configure for Availability")}
                </Typography>
            )}
            {!shouldHideInventoryForBundleProducts && (
                <ProductContextAvailability
                    isProductDetailsPage={true}
                    configurationCompleted={configurationCompleted}
                    extendedStyles={styles.availability}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsPrice)),
    definition: {
        displayName: "Price",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
